import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { DataBody, BrainField, Vertebra, Leg, LegNode, VertebraTemplate, LegNodeTemplate, Client, isDeliveryNodeType } from '../../types';
import * as api from '../../services/api';
import BrainSection from '../Brain/BrainSection';
import SpineSection from '../Vertebrae/SpineSection';
import LegsSection from '../Legs/LegsSection';
import BuildDataBodyForm, { BuildFormState, emptyBuildForm, createFromBuildForm } from './BuildDataBodyForm';
import ClientSelect, { ClientSelection } from '../shared/ClientSelect';

// Default templates (used when API is not available)
const DEFAULT_VERTEBRAE_TEMPLATES: VertebraTemplate[] = [
  { type: 'email_oversight_validation', name: 'Email Oversight - Email Validation', description: 'Paid email validation via Email Oversight (emailoversight.com). BYO API Token + List ID; configure which result codes count as a pass.', icon: 'shield-check', category: 'validation', requiresApiSetup: true },
  { type: 'free_email_check', name: 'Free Email Check', description: 'Free in-house sanity check for email addresses (format, disposable domains, role-based). Catches obvious junk before paid validation.', icon: 'mail-off', category: 'validation', requiresApiSetup: false },
  { type: 'free_phone_check', name: 'Free Phone Check', description: 'Free in-house sanity check for phone numbers (NANP rules + international length). Catches obvious junk before paid validation.', icon: 'phone-off', category: 'validation', requiresApiSetup: false },
  { type: 'filter_rule', name: 'Filter Rule', description: 'Filter leads based on field values (no API required)', icon: 'filter', category: 'filter', requiresApiSetup: false },
  { type: 'dedup_check', name: 'Duplicate Check', description: 'Check for duplicate leads based on specified fields', icon: 'copy-slash', category: 'filter', requiresApiSetup: false },
  { type: 'email_suppression', name: 'Email Suppression', description: 'Block leads whose email matches your account-level suppression list. Shared across every data body in your account — perfect for opt-outs and do-not-contact.', icon: 'mail-x', category: 'filter', requiresApiSetup: false },
  { type: 'phone_suppression', name: 'Phone Suppression', description: 'Block leads whose phone matches your account-level suppression list. Shared across every data body in your account — perfect for opt-outs and do-not-call.', icon: 'phone-off', category: 'filter', requiresApiSetup: false },
  // BigSky Real Time Data Enrichment — gated behind REACT_APP_BIGSKY_ENABLED
  // so it ships dark and only appears in the builder once a live key is
  // provisioned and tested. The backend processor is also gated
  // (config.bigsky.enabled), so even if this entry is shown the vertebra
  // no-ops as a pass-through until the server is enabled too.
  ...(import.meta.env.REACT_APP_BIGSKY_ENABLED === 'true'
    ? ([{ type: 'bigsky_enrichment', name: 'BigSky Real Time Data Enrichment', description: 'Append verified identity, demographic, financial and B2B attributes in real time. Choose what to enrich from (Email, Phone, MD5 or IP) and a-la-carte the attributes you want. Unmatched or blank leads pass straight through — never blocked.', icon: 'sparkles', category: 'enrichment', requiresApiSetup: false }] as VertebraTemplate[])
    : []),
  // Owned Data Enrichment — same builder UX as BigSky, served from our own
  // identity database. Gated behind REACT_APP_OWNED_ENRICH_ENABLED so it
  // ships dark; the backend processor is also gated
  // (config.ownedEnrichment.enabled), so it no-ops as a pass-through until
  // the server is enabled too.
  ...(import.meta.env.REACT_APP_OWNED_ENRICH_ENABLED === 'true'
    ? ([{ type: 'owned_enrichment', name: 'Identity Enrichment', description: 'Append verified identity, demographic, financial and B2B attributes in real time from our own identity database. Choose what to enrich from (Email, Phone, MD5 or IP) and a-la-carte the attributes you want. Unmatched or blank leads pass straight through — never blocked.', icon: 'sparkles', category: 'enrichment', requiresApiSetup: false }] as VertebraTemplate[])
    : []),
];

const DEFAULT_LEG_TEMPLATES: LegNodeTemplate[] = [
  { type: 'custom_api', name: 'Custom API', description: 'Push lead data to any API endpoint', icon: 'plug' },
  { type: 'time_delay', name: 'Time Delay', description: 'Wait a specified amount of time before continuing', icon: 'clock' },
  { type: 'condition', name: 'Condition', description: 'Branch based on a lead field value', icon: 'git-branch' },
  { type: 'csv_download', name: '.CSV File Download', description: 'Manually download leads as a CSV. Does NOT receive incoming leads — you trigger downloads on demand.', icon: 'download' },
  { type: 'cloud_data_destination', name: 'Cloud Data Destination', description: 'Deliver a daily file of leads to a cloud bucket (Amazon S3, Cloudflare R2, or Google Cloud Storage). One file per day, cut on the account timezone. Credentials encrypted at rest.', icon: 'cloud-upload' },
  { type: 'google_sheets', name: 'Google Sheets', description: 'Append each lead as a row in a client Google Sheet. Client shares their sheet with our service account — no keys to manage. Rows are batched under Google’s API quota so it can never rate-limit, with zero dropped leads.', icon: 'table' },
  { type: 'sftp_ftp', name: 'SFTP & FTP', description: 'Automatically deliver a CSV of leads to a client SFTP or FTP server on a Daily (last 24h) or Weekly (last 7 days) schedule, cut on the account timezone. Same Desired / Fields / Filter options as the .CSV download. Credentials encrypted at rest.', icon: 'server' },
];

// Build-status glow for the palette. GREEN = a purpose-built vertebra/leg we
// finished and hardened together (dedicated processor + verified live). RED =
// scaffolding that isn't production-ready yet — a visual reminder not to reach
// for it. Keyed by template `type`; anything NOT listed here as ready falls
// through to the red glow.
//
// 2026-09-13: the original-build red templates (email_validation,
// phone_validation, address_validation, data_enrichment, database_lookup,
// custom_api vertebra, format_transform; webhook_output and split legs) were
// removed from the palette entirely. New templates are added manually and stay
// red until listed below.
const PRODUCTION_READY_VERTEBRAE = new Set<string>([
  'email_oversight_validation',
  'free_email_check',
  'free_phone_check',
  'filter_rule',
  'dedup_check',
  'email_suppression',
  'phone_suppression',
  'bigsky_enrichment',
  'owned_enrichment',
]);

const PRODUCTION_READY_LEGS = new Set<string>([
  'custom_api',
  'time_delay',
  'condition',
  'csv_download',
  'cloud_data_destination',
  'google_sheets',
  'sftp_ftp',
]);

// Subtle ring + outer glow. Green = ready, red = still needs work.
const READY_GLOW = '0 0 0 1.5px rgba(34,197,94,0.55), 0 0 9px 2px rgba(34,197,94,0.35)';
const NOT_READY_GLOW = '0 0 0 1.5px rgba(239,68,68,0.5), 0 0 9px 2px rgba(239,68,68,0.30)';

function DataBodyBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;

  const [dataBody, setDataBody] = useState<DataBody | null>(null);
  const [brainFields, setBrainFields] = useState<BrainField[]>([]);
  const [vertebrae, setVertebrae] = useState<Vertebra[]>([]);
  const [legs, setLegs] = useState<Leg[]>([]);
  const [legNodes, setLegNodes] = useState<LegNode[]>([]);
  // Account-level client directory, loaded once so every leg-node card can
  // show its client's NAME without a fetch per card.
  const [clients, setClients] = useState<Client[]>([]);
  // A delivery node the user has asked for but which does NOT exist yet: a
  // client is required to create one, so the template is parked here until
  // they pick one. Creating the node first and asking after would leave an
  // unassigned live destination behind if they cancelled.
  const [pendingNode, setPendingNode] = useState<
    { legId: string | undefined; template: LegNodeTemplate; parentNodeId?: string } | null
  >(null);
  // Stats live on a separate, slower endpoint (GET /:id/stats) so the
  // builder UI can render the moment metadata arrives. BrainSection
  // displays a skeleton in place of the totals while `statsLoading`.
  const [stats, setStats] = useState<Record<string, number> | null>(null);
  const [statsToday, setStatsToday] = useState<Record<string, number> | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [vertebraeTemplates] = useState<VertebraTemplate[]>(DEFAULT_VERTEBRAE_TEMPLATES);
  const [legTemplates] = useState<LegNodeTemplate[]>(DEFAULT_LEG_TEMPLATES);
  const [loading, setLoading] = useState(!isNew);
  // Shared with the Dashboard's create modal — same fields, same submit
  // path, so a body built from either entry point is configured identically.
  const [buildForm, setBuildForm] = useState<BuildFormState>(emptyBuildForm);
  const [createError, setCreateError] = useState('');

  const loadClients = useCallback(async () => {
    try {
      setClients(await api.getClients());
    } catch (err) {
      // Non-fatal: cards fall back to showing no client name.
      console.error('Failed to load clients:', err);
    }
  }, []);

  const loadDataBody = useCallback(async (bodyId: string) => {
    // Metadata (instant) + stats (slow) fired in parallel. Page paints
    // as soon as metadata resolves; stats trickle in and replace the
    // skeleton in BrainSection's totals row.
    const metadataPromise = api.getDataBody(bodyId);
    const statsPromise = api.getDataBodyStats(bodyId).catch(err => {
      console.warn('Failed to load body stats:', err);
      return null;
    });

    try {
      const body = await metadataPromise;
      setDataBody(body);
      setBrainFields(body.brainFields || []);
      setVertebrae(body.vertebrae || []);
      setLegs(body.legs || []);
      setLegNodes(body.legNodes || []);
      void loadClients();
    } catch (err) {
      console.error('Failed to load data body:', err);
    } finally {
      setLoading(false);
    }

    // Resolve stats independently so a slow stats response doesn't
    // delay the rest of the page.
    const s = await statsPromise;
    if (s) {
      setStats(s.stats);
      setStatsToday(s.statsToday);
    }
    setStatsLoading(false);
  }, [loadClients]);

  // Load existing data body
  useEffect(() => {
    if (id) {
      void loadDataBody(id);
    }
  }, [id, loadDataBody]);

  // Create new data body
  async function handleCreate() {
    setCreateError('');
    try {
      const body = await createFromBuildForm(buildForm);
      void navigate(`/body/${body.id}`, { replace: true });
    } catch (err: any) {
      const detail = err.response?.data?.detail || err.response?.data?.error || err.message || 'Unknown error';
      console.error('Failed to create:', detail);
      setCreateError(detail);
    }
  }

  // --- Vertebrae operations ---

  async function addVertebra(template: VertebraTemplate) {
    if (!dataBody) {return;}
    try {
      const vertebra = await api.createVertebra(dataBody.id, {
        type: template.type,
        name: template.name,
        config: {},
        onFail: 'stop',
      });
      setVertebrae(prev => [...prev, vertebra]);
    } catch (err) {
      console.error('Failed to add vertebra:', err);
    }
  }

  async function removeVertebra(vertebraId: string) {
    if (!dataBody) {return;}
    try {
      await api.deleteVertebra(dataBody.id, vertebraId);
      setVertebrae(prev => prev.filter(v => v.id !== vertebraId));
    } catch (err) {
      console.error('Failed to remove vertebra:', err);
    }
  }

  async function toggleVertebra(vertebraId: string) {
    if (!dataBody) {return;}
    const vertebra = vertebrae.find(v => v.id === vertebraId);
    if (!vertebra) {return;}
    try {
      const updated = await api.updateVertebra(dataBody.id, vertebraId, { isActive: !vertebra.isActive });
      setVertebrae(prev => prev.map(v => v.id === vertebraId ? updated : v));
    } catch (err) {
      console.error('Failed to toggle vertebra:', err);
    }
  }

  async function updateVertebraConfig(vertebraId: string, config: Record<string, any>) {
    if (!dataBody) {return;}
    try {
      const updated = await api.updateVertebra(dataBody.id, vertebraId, { config });
      setVertebrae(prev => prev.map(v => v.id === vertebraId ? updated : v));
    } catch (err) {
      console.error('Failed to update vertebra config:', err);
    }
  }

  // --- Leg (container) operations ---

  async function addLeg(name: string) {
    if (!dataBody) {return;}
    try {
      const leg = await api.createLeg(dataBody.id, { name });
      setLegs(prev => [...prev, leg]);
    } catch (err) {
      console.error('Failed to add leg:', err);
    }
  }

  async function renameLeg(legId: string, name: string) {
    if (!dataBody) {return;}
    try {
      const updated = await api.updateLeg(dataBody.id, legId, { name });
      setLegs(prev => prev.map(l => l.id === legId ? updated : l));
    } catch (err) {
      console.error('Failed to rename leg:', err);
    }
  }

  async function toggleLegActive(legId: string, isActive: boolean) {
    if (!dataBody) {return;}
    try {
      const updated = await api.updateLeg(dataBody.id, legId, { isActive });
      setLegs(prev => prev.map(l => l.id === legId ? updated : l));
    } catch (err) {
      console.error('Failed to toggle leg:', err);
    }
  }

  async function removeLeg(legId: string) {
    if (!dataBody) {return;}
    try {
      await api.deleteLeg(dataBody.id, legId);
      setLegs(prev => prev.filter(l => l.id !== legId));
      // Backend cascades the deletion of leg_nodes inside this leg, so prune
      // them from local state too.
      setLegNodes(prev => prev.filter(n => n.legId !== legId));
    } catch (err) {
      console.error('Failed to remove leg:', err);
    }
  }

  // Copy a leg (container + full node tree) within THIS data body ("Copy Leg
  // In"). The backend returns the new inactive leg plus its nodes, which we
  // splice straight into local state so the copy appears immediately.
  async function copyLegIn(legId: string) {
    if (!dataBody) {return;}
    try {
      const { leg, legNodes: newNodes } = await api.cloneLeg(dataBody.id, legId);
      setLegs(prev => [...prev, leg]);
      setLegNodes(prev => [...prev, ...newNodes]);
    } catch (err) {
      console.error('Failed to copy leg in:', err);
    }
  }

  // Copy a leg OUT to a different data body. Nothing in the current view
  // changes (the copy lands in the target body), so we just resolve/reject.
  async function copyLegOut(legId: string, targetDataBodyId: string) {
    if (!dataBody) {return;}
    try {
      await api.cloneLeg(dataBody.id, legId, { targetDataBodyId });
    } catch (err) {
      console.error('Failed to copy leg out:', err);
      throw err;
    }
  }

  // Other data bodies in the org, for the "Copy Leg Out" picker. Fetched
  // lazily the first time a leg menu needs it; excludes the current body.
  // Status is carried through so the picker can split Active vs Archived.
  async function loadCopyTargets(): Promise<{ id: string; name: string; status: DataBody['status'] }[]> {
    if (!dataBody) {return [];}
    const bodies = await api.getDataBodies();
    return bodies
      .filter(b => b.id !== dataBody.id)
      .map(b => ({ id: b.id, name: b.name, status: b.status }));
  }

  // --- Leg node operations ---
  // legId is now a first-class arg. Pass undefined to defer to the backend's
  // findOrCreateDefault path (used only by the empty-state legacy drop zone).

  async function addLegNode(
    legId: string | undefined,
    template: LegNodeTemplate,
    parentNodeId?: string,
    client?: ClientSelection
  ) {
    if (!dataBody) {return;}

    // Every NEW delivery node must be assigned a client. Park the request and
    // ask first rather than creating an unassigned destination. Nodes that
    // predate clients keep working untouched — this gate is only on create.
    if (isDeliveryNodeType(template.type) && !client) {
      setPendingNode({ legId, template, parentNodeId });
      return;
    }

    try {
      const node = await api.createLegNode(dataBody.id, {
        type: template.type,
        name: template.name,
        parentNodeId: parentNodeId || undefined,
        config: {},
        legId,
        clientId: client?.kind === 'existing' ? client.clientId : undefined,
        clientName: client?.kind === 'new' ? client.name : undefined,
      });
      setLegNodes(prev => [...prev, node]);
      setPendingNode(null);
      // A brand-new client may have just been created by name, so refresh the
      // directory to pick it up for the card label and the next picker.
      if (client?.kind === 'new') {void loadClients();}
      // If the backend auto-created a leg (legId omitted on first drop into
      // an empty data body), pick it up so the UI re-renders with the new leg.
      if (!legId && !legs.some(l => l.id === node.legId)) {
        try {
          const refreshed = await api.getLegs(dataBody.id);
          setLegs(refreshed);
        } catch { /* non-fatal */ }
      }
    } catch (err) {
      console.error('Failed to add leg node:', err);
    }
  }

  /**
   * Re-read the nodes and the client directory together. Called after a
   * client is assigned inside a node's config modal, because that write
   * happens in the modal (not through onSave) and can also have created a
   * brand-new client that no card knows the name of yet.
   */
  const refreshNodesAndClients = useCallback(async () => {
    if (!dataBody) {return;}
    try {
      const [nodes] = await Promise.all([api.getLegNodes(dataBody.id), loadClients()]);
      setLegNodes(nodes);
    } catch (err) {
      console.error('Failed to refresh leg nodes:', err);
    }
  }, [dataBody, loadClients]);

  async function removeLegNode(nodeId: string) {
    if (!dataBody) {return;}
    try {
      await api.deleteLegNode(dataBody.id, nodeId);
      setLegNodes(prev => prev.filter(n => n.id !== nodeId));
    } catch (err) {
      console.error('Failed to remove leg node:', err);
    }
  }

  async function updateLegNodeConfig(nodeId: string, config: Record<string, any>) {
    if (!dataBody) {return;}
    try {
      const updated = await api.updateLegNode(dataBody.id, nodeId, { config });
      setLegNodes(prev => prev.map(n => n.id === nodeId ? updated : n));
    } catch (err) {
      console.error('Failed to update leg node config:', err);
    }
  }

  // --- Brain field operations ---

  async function addBrainField(fieldName: string, fieldLabel: string, fieldType: string) {
    if (!dataBody) {return;}
    try {
      const field = await api.createBrainField(dataBody.id, { fieldName, fieldLabel, fieldType });
      setBrainFields(prev => [...prev, field]);
    } catch (err) {
      console.error('Failed to add brain field:', err);
    }
  }

  async function toggleFieldRequired(fieldId: string) {
    if (!dataBody) {return;}
    const field = brainFields.find(f => f.id === fieldId);
    if (!field) {return;}
    try {
      const updated = await api.updateBrainField(dataBody.id, fieldId, { isRequired: !field.isRequired });
      setBrainFields(prev => prev.map(f => f.id === fieldId ? updated : f));
    } catch (err) {
      console.error('Failed to toggle field required:', err);
    }
  }

  async function removeBrainField(fieldId: string) {
    if (!dataBody) {return;}
    try {
      await api.deleteBrainField(dataBody.id, fieldId);
      setBrainFields(prev => prev.filter(f => f.id !== fieldId));
    } catch (err) {
      console.error('Failed to remove brain field:', err);
    }
  }

  // --- Drag and Drop ---

  async function handleDragEnd(result: DropResult) {
    if (!result.destination || !dataBody) {return;}

    const { source, destination, draggableId } = result;

    // Reordering vertebrae within the spine
    if (source.droppableId === 'spine' && destination.droppableId === 'spine') {
      const reordered = Array.from(vertebrae);
      const [moved] = reordered.splice(source.index, 1);
      reordered.splice(destination.index, 0, moved);
      setVertebrae(reordered);

      // Persist the new order
      try {
        await api.reorderVertebrae(dataBody.id, reordered.map(v => v.id));
      } catch (err) {
        console.error('Failed to reorder:', err);
        // Reload to get correct order
        void loadDataBody(dataBody.id);
      }
    }

    // Dragging a template into the spine
    if (source.droppableId === 'vertebrae-templates' && destination.droppableId === 'spine') {
      const template = vertebraeTemplates.find(t => t.type === draggableId);
      if (template) {
        await addVertebra(template);
      }
    }

    // Dragging a leg template into the legs area.
    // NOTE: leg draggableIds are prefixed with "leg-" (see the Draggable in the
    // leg-templates Droppable) because some types collide with vertebrae types
    // (e.g. "custom_api" exists in both), and @hello-pangea/dnd requires globally
    // unique draggableIds. Strip the prefix before looking up the template.
    //
    // Multi-leg drop targets:
    //   "legs"          → empty-state drop zone (no legs yet); backend auto-
    //                      creates Leg 1 via findOrCreateDefault.
    //   "leg:<legId>"   → drop into a specific leg's column.
    if (source.droppableId === 'leg-templates' &&
        (destination.droppableId === 'legs' || destination.droppableId.startsWith('leg:'))) {
      const type = draggableId.replace(/^leg-/, '');
      const template = legTemplates.find(t => t.type === type);
      if (template) {
        const legId = destination.droppableId.startsWith('leg:')
          ? destination.droppableId.slice(4)
          : undefined;
        await addLegNode(legId, template);
      }
    }
  }

  // --- New data body creation screen ---
  if (isNew) {
    return (
      <div>
        <div className="page-header">
          <div>
            <h1 className="page-title">Build New Data Body</h1>
            <p className="page-subtitle">Name it, say who's sending the leads, and set its limits</p>
          </div>
        </div>
        <div style={{ maxWidth: 720 }}>
          <BuildDataBodyForm
            value={buildForm}
            onChange={setBuildForm}
            onSubmit={handleCreate}
            autoFocus
          />
          {createError && (
            <div style={{ padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#dc2626', fontSize: 13, marginTop: 12 }}>
              {createError}
            </div>
          )}
          {/* Same actions, same labels as the Dashboard modal — this page and
              that modal are the same form, so they must not disagree. */}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button className="btn btn-secondary" onClick={() => navigate('/')}>Cancel</button>
            <button className="btn btn-primary" onClick={handleCreate}>Create Data Body</button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading data body...</div>;
  }

  if (!dataBody) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#ef4444' }}>Data body not found</div>;
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div>
        {/* Header */}
        <div className="page-header">
          <div>
            <h1 className="page-title">{dataBody.name}</h1>
            <p className="page-subtitle">
              Status: <span className={`badge badge-${dataBody.status === 'active' ? 'success' : 'warning'}`}>
                {dataBody.status}
              </span>
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-secondary" onClick={() => navigate('/')}>
              Back to Dashboard
            </button>
          </div>
        </div>

        <div className="builder-layout">
          {/* Sidebar: Templates */}
          <div className="builder-sidebar">
            {/* Vertebrae Templates */}
            <div className="builder-panel">
              <div className="builder-panel-title">Vertebrae (Drag to Spine)</div>
              <Droppable droppableId="vertebrae-templates" isDropDisabled={true}>
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {vertebraeTemplates.map((template, index) => (
                      <Draggable key={template.type} draggableId={template.type} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className="template-item"
                            style={{
                              ...provided.draggableProps.style,
                              opacity: snapshot.isDragging ? 0.8 : 1,
                              boxShadow: PRODUCTION_READY_VERTEBRAE.has(template.type)
                                ? READY_GLOW
                                : NOT_READY_GLOW,
                            }}
                          >
                            <div className={`template-icon ${template.category}`}>
                              {getTemplateEmoji(template.type)}
                            </div>
                            <div>
                              <div style={{ fontWeight: 500, fontSize: 13 }}>{template.name}</div>
                              <div style={{ fontSize: 11, color: '#9ca3af' }}>{template.category}</div>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>

            {/* Leg Templates */}
            <div className="builder-panel">
              <div className="builder-panel-title">Legs (Drag to Decision Tree)</div>
              <Droppable droppableId="leg-templates" isDropDisabled={true}>
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {legTemplates.map((template, index) => (
                      <Draggable key={template.type} draggableId={`leg-${template.type}`} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className="template-item"
                            style={{
                              ...provided.draggableProps.style,
                              opacity: snapshot.isDragging ? 0.8 : 1,
                              boxShadow: PRODUCTION_READY_LEGS.has(template.type)
                                ? READY_GLOW
                                : NOT_READY_GLOW,
                            }}
                          >
                            <div className="template-icon" style={{ background: '#d1fae5', color: '#059669' }}>
                              {getLegEmoji(template.type)}
                            </div>
                            <div>
                              <div style={{ fontWeight: 500, fontSize: 13 }}>{template.name}</div>
                              <div style={{ fontSize: 11, color: '#9ca3af' }}>{template.description}</div>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          </div>

          {/* Main Builder Area: Body Visualization */}
          <div className="builder-main">
            <div className="body-container">
              {/* BRAIN */}
              <BrainSection
                dataBody={dataBody}
                fields={brainFields}
                stats={stats}
                statsToday={statsToday}
                statsLoading={statsLoading}
                onAddField={addBrainField}
                onToggleRequired={toggleFieldRequired}
                onRemoveField={removeBrainField}
              />

              <div className="body-connector" />

              {/* SPINE (Vertebrae) */}
              <SpineSection
                vertebrae={vertebrae}
                templates={vertebraeTemplates}
                onRemove={removeVertebra}
                onToggle={toggleVertebra}
                onUpdateConfig={updateVertebraConfig}
              />

              <div className="body-connector" />

              {/* LEGS (Decision Tree) */}
              <LegsSection
                legs={legs}
                legNodes={legNodes}
                templates={legTemplates}
                onAddLeg={addLeg}
                onRenameLeg={renameLeg}
                onToggleLegActive={toggleLegActive}
                onDeleteLeg={removeLeg}
                onCopyLegIn={copyLegIn}
                onCopyLegOut={copyLegOut}
                loadCopyTargets={loadCopyTargets}
                onAddNode={(legId, template, parentNodeId) => addLegNode(legId, template, parentNodeId)}
                onRemoveNode={removeLegNode}
                onUpdateNodeConfig={updateLegNodeConfig}
                clients={clients}
                onClientAssigned={refreshNodesAndClients}
              />

              {/* Client gate for a new delivery node. Shown BEFORE the node
                  exists — see addLegNode. */}
              {pendingNode && (
                <NewNodeClientModal
                  templateName={pendingNode.template.name}
                  onCancel={() => setPendingNode(null)}
                  onConfirm={(selection) =>
                    addLegNode(
                      pendingNode.legId,
                      pendingNode.template,
                      pendingNode.parentNodeId,
                      selection
                    )
                  }
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </DragDropContext>
  );
}

/**
 * The client gate for a NEW delivery node.
 *
 * Shown before the node is created, not after, because a delivery node
 * without a client is exactly the state the product forbids — creating first
 * and asking second would leave one behind every time someone cancelled.
 *
 * Once the node exists and has delivered a lead the assignment is frozen
 * forever, so this dialog says so plainly rather than letting someone
 * discover it later.
 */
function NewNodeClientModal({
  templateName,
  onCancel,
  onConfirm,
}: {
  templateName: string;
  onCancel: () => void;
  onConfirm: (selection: ClientSelection) => void;
}) {
  const [selection, setSelection] = useState<ClientSelection>(null);

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460, width: '100%' }}>
        <h2 className="modal-title">Who receives these leads?</h2>
        <p style={{ marginTop: 4, marginBottom: 16, fontSize: 13, color: '#6b7280' }}>
          Every new <strong>{templateName}</strong> destination needs a client. Pick one,
          or type a new name to create it.
        </p>

        <div className="form-group">
          <label className="form-label">Client</label>
          <ClientSelect value={selection} onChange={setSelection} autoFocus />
        </div>

        <div style={{
          padding: '9px 12px',
          background: '#fffbeb',
          border: '1px solid #fde68a',
          borderRadius: 6,
          color: '#92400e',
          fontSize: 12,
        }}>
          You can change this until the first lead is delivered here. After that it is locked for good.
        </div>

        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
          <button
            className="btn btn-primary"
            disabled={!selection}
            onClick={() => onConfirm(selection)}
          >
            Add destination
          </button>
        </div>
      </div>
    </div>
  );
}

// Simple emoji mapping for template icons
function getTemplateEmoji(type: string): string {
  const map: Record<string, string> = {
    email_validation: '\u2709',
    email_oversight_validation: '\uD83D\uDEE1',
    free_email_check: '\uD83C\uDD93',
    phone_validation: '\u260E',
    free_phone_check: '\uD83C\uDD93',
    address_validation: '\uD83D\uDCCD',
    data_enrichment: '\uD83D\uDCCA',
    database_lookup: '\uD83D\uDD0D',
    custom_api: '\uD83D\uDD0C',
    filter_rule: '\uD83D\uDCCB',
    dedup_check: '\uD83D\uDDC2',
    format_transform: '\u2728',
  };
  return map[type] || '\u2699';
}

function getLegEmoji(type: string): string {
  const map: Record<string, string> = {
    webhook_output: '\ud83d\udce4',
    custom_api: '\ud83d\udd0c',
    time_delay: '\u23f0',
    condition: '\ud83d\udd00',
    split: '\ud83d\udd01',
    // .CSV File Download \u2014 down-arrow matches the LegContainer card icon
    csv_download: '\u2b07',
    // Cloud Data Destination \u2014 cloud-with-arrow (file drop to a bucket)
    cloud_data_destination: '\u2601',
    // Google Sheets \u2014 bar-chart/table glyph
    google_sheets: '\ud83d\udcca',
    // SFTP & FTP \u2014 server/drive glyph
    sftp_ftp: '\ud83d\uddc4',
  };
  return map[type] || '\u2699';
}

export default DataBodyBuilder;
